/**
 * Small animation pieces used across the inquiry form. They build on the
 * shared presets in components/motion and fall back to a plain fade (or no
 * movement at all) when the guest prefers reduced motion.
 */
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, animate, motion, useReducedMotion } from "framer-motion";
import { DURATION, EASE_OUT } from "../../../../components/motion";
import { cn } from "../../../../utils/classnames";
import { formatYen } from "../pricing";

/**
 * Text that slides up and fades when its value changes, e.g. a summary row
 * going from "-" to "AB123". `mode="wait"` lets the old text leave first so
 * the two never overlap.
 */
export const SwapText = ({ value, className }) => {
  const shouldReduceMotion = useReducedMotion();
  const travel = shouldReduceMotion ? 0 : 6;

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.span
        key={value}
        className={cn("tw:inline-block", className)}
        initial={{ opacity: 0, y: travel }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -travel }}
        transition={{ duration: DURATION.fast * 0.7, ease: EASE_OUT }}
      >
        {value}
      </motion.span>
    </AnimatePresence>
  );
};

/** How long the price takes to count to its new value, in seconds. */
const PRICE_COUNT_DURATION = 0.6;

/**
 * "21,500 JPY" that counts up or down to a new amount instead of jumping,
 * so the guest sees the total react to what they change.
 */
export const AnimatedYen = ({ amount, className }) => {
  const shouldReduceMotion = useReducedMotion();
  const [displayedAmount, setDisplayedAmount] = useState(amount);
  const displayedAmountRef = useRef(amount);

  useEffect(() => {
    if (shouldReduceMotion) {
      displayedAmountRef.current = amount;
      setDisplayedAmount(amount);
      return undefined;
    }
    const counting = animate(displayedAmountRef.current, amount, {
      duration: PRICE_COUNT_DURATION,
      ease: EASE_OUT,
      onUpdate: (latestAmount) => {
        displayedAmountRef.current = latestAmount;
        setDisplayedAmount(Math.round(latestAmount));
      },
    });
    // A newer amount stops this count where it is and starts from there.
    return () => counting.stop();
  }, [amount, shouldReduceMotion]);

  return <span className={cn("tw:tabular-nums", className)}>{formatYen(displayedAmount)}</span>;
};

/**
 * The dot inside a selected radio mark: it pops in with a small spring and
 * shrinks away when the option is deselected.
 */
export const RadioDot = ({ isVisible }) => {
  const shouldReduceMotion = useReducedMotion();
  return (
    <AnimatePresence initial={false}>
      {isVisible && (
        <motion.span
          className="tw:size-[8.5px] tw:!rounded-full tw:bg-[#282828]"
          initial={{ scale: shouldReduceMotion ? 1 : 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: shouldReduceMotion ? 1 : 0, opacity: 0 }}
          transition={{ type: "spring", stiffness: 500, damping: 28 }}
        />
      )}
    </AnimatePresence>
  );
};

/**
 * Opens and closes a block by animating its height (like the shared
 * Collapse), but only clips its content *while* it moves. Once open, the
 * content may overflow -- the phone country list and date pickers inside
 * these panels pop out below and must not be cut off.
 */
export const Fold = ({ isOpen, children, className }) => {
  const shouldReduceMotion = useReducedMotion();
  const [isMoving, setIsMoving] = useState(false);

  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          className={className}
          style={{ overflow: isMoving ? "hidden" : "visible" }}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={shouldReduceMotion ? { duration: 0 } : { duration: DURATION.fast, ease: EASE_OUT }}
          onAnimationStart={() => setIsMoving(true)}
          onAnimationComplete={() => setIsMoving(false)}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/** Press feedback for buttons: a slight lift on hover, a squeeze on tap. */
export const usePressMotion = (hoverScale = 1.01, tapScale = 0.98) => {
  const shouldReduceMotion = useReducedMotion();
  if (shouldReduceMotion) return {};
  return {
    whileHover: { scale: hoverScale },
    whileTap: { scale: tapScale },
    transition: { duration: 0.15, ease: EASE_OUT },
  };
};
